import { useCallback, useEffect, useMemo, useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FlightsFilterForm } from 'legacyfrontend/components/filters';
import { useTranslation } from 'react-i18next';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';

import PackagesApi from 'legacyfrontend/api/paquetes';
import { DATE_KEYS, ROUTE_KEYS } from 'legacyfrontend/constants';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import { useFlightContext } from 'legacyfrontend/contexts/FlightDetailsContext';
import { formatWithMomentFormat } from 'legacyfrontend/utils/dateUtils';

import { getCacheStatus } from 'legacyfrontend/helpers/getCacheStatus';
import ErrorSnackbar from '../../ErrorSnackbar';

const { BUSCAR_X_VUELOS } = ROUTE_KEYS;

const parseDateFromURL = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0, 0);
  return date;
};

const formatDateLocal = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const dateToLocalString = (date) => {
  if (!date) return null;
  return formatDateLocal(date);
};

const dateFromLocalString = (dateString) => {
  if (!dateString) return null;
  return parseDateFromURL(dateString);
};

const initialFlightSetter = (params) => {
  const passengersParam = params?.get('passengers');
  const parsedPassengers = passengersParam ? passengersParam.split(',').map(Number) : [30];

  const result = {
    origin:
      params?.get('originId') && params?.get('originName')
        ? {
          id:
            params.get('originIdn') && !isNaN(Number(params.get('originIdn')))
              ? Number(params.get('originIdn'))
              : null,
          cityCode: params.get('originId'),
          cityName: params.get('originName').trim(),
        }
        : null,
    destination:
      params?.get('destinationId') && params?.get('destinationName')
        ? {
          id:
            params.get('destinationIdn') && !isNaN(Number(params.get('destinationIdn')))
              ? Number(params.get('destinationIdn'))
              : null,
          cityCode: params.get('destinationId'),
          cityName: params.get('destinationName').trim(),
        }
        : null,
    passengers: parsedPassengers,
    departureDate: parseDateFromURL(params?.get('departureDate')),
    returnDate: parseDateFromURL(params?.get('returnDate')),
    direct: false,
    baggage: false,
    cabin: 'Y',
    currency: params?.get('currency') || 'USD',
    isOnlyOneWay: params?.get('isOnlyOneWay') === 'true',
  };

  return result;
};

const FlightsSearch = forwardRef(
  (
    {
      isOnTargetPath,
      background,
      text,
      onCloseMobileSearch,
      onSearchStart,
      onSearchComplete,
      isMobile,
      isLoading = false,
    },
    ref
  ) => {
    const { t } = useTranslation('layout');
    const history = useHistory();
    const { pathname, search } = useLocation();
    const { registerOnSearch } = useGlobal();
    const { setOneWayFlight, isLoading: flightContextLoading } = useFlightContext();

    const [isUpdatingFlightInfo, setIsUpdatingFlightInfo] = useState(false);
    const isUpdatingFlightInfoRef = useRef(false);
    const lastUpdateFlightInfoRef = useRef({ originId: null, destinationId: null, timestamp: 0 });
    const updateFlightInfoCallCountRef = useRef(0);
    const updateFlightInfoMaxCallsRef = useRef(0);
    const isBlockedPermanentlyRef = useRef(false);

    const [errorMessage, setErrorMessage] = useState('');
    const [openToast, setOpenToast] = useState(false);

    const [isMobileSearchActive, setIsMobileSearchActive] = useState(false);
    const [hasAutoSearched, setHasAutoSearched] = useState(false);
    const hasAutoSearchedRef = useRef(false);
    const [searchTimestamp, setSearchTimestamp] = useState(null);
    const lastAutoSearchRef = useRef({ timestamp: 0, params: null });
    const isSearchingRef = useRef(false);
    const isAutoSearchingRef = useRef(false);

    const initialFlightFormInput = useMemo(
      () =>
        initialFlightSetter(
          !!search && pathname === BUSCAR_X_VUELOS ? new URLSearchParams(search) : undefined
        ),
      [search, pathname]
    );

    const getInitialState = () => {
      if (isClient) {
        try {
          const flightInfo = sessionStorage.getItem('flightInfo');
          if (flightInfo) {
            const parsed = JSON.parse(flightInfo);
            const restoredState = {
              origin: parsed.origin ? {
                id: parsed.origin.id,
                cityCode: parsed.origin.cityCode,
                cityName: parsed.origin.cityName,
                airportCode: parsed.origin.airportCode,
                airportName: parsed.origin.airportName,
                countryCode: parsed.origin.countryCode
              } : null,
              destination: parsed.destination ? {
                id: parsed.destination.id,
                cityCode: parsed.destination.cityCode,
                cityName: parsed.destination.cityName,
                airportCode: parsed.destination.airportCode,
                airportName: parsed.destination.airportName,
                countryCode: parsed.destination.countryCode
              } : null,
              passengers: parsed.passengers || [30],
              departureDate: parsed.departureDate ? dateFromLocalString(parsed.departureDate) : null,
              returnDate: parsed.returnDate ? dateFromLocalString(parsed.returnDate) : null,
              isOnlyOneWay: parsed.isOnlyOneWay || false,
              direct: false,
              baggage: false,
              cabin: 'Y',
              currency: parsed.currency || 'USD'
            };
            return restoredState;
          }
        } catch (error) {
        }
      }
      return initialFlightFormInput;
    };

    const [flightFormInput, setFlightFormInput] = useState(getInitialState);

    useEffect(() => {
      if (isClient) {
        try {
          const flightInfo = sessionStorage.getItem('flightInfo');
          if (flightInfo) {
            const parsed = JSON.parse(flightInfo);
            if (parsed.origin || parsed.destination || parsed.departureDate) {
              setFlightFormInput(prev => {
                const newState = {
                  ...prev,
                  origin: parsed.origin ? {
                    id: parsed.origin.id,
                    cityCode: parsed.origin.cityCode,
                    cityName: parsed.origin.cityName,
                    airportCode: parsed.origin.airportCode,
                    airportName: parsed.origin.airportName,
                    countryCode: parsed.origin.countryCode
                  } : prev.origin,
                  destination: parsed.destination ? {
                    id: parsed.destination.id,
                    cityCode: parsed.destination.cityCode,
                    cityName: parsed.destination.cityName,
                    airportCode: parsed.destination.airportCode,
                    airportName: parsed.destination.airportName,
                    countryCode: parsed.destination.countryCode
                  } : prev.destination,
                  passengers: parsed.passengers || prev.passengers,
                  departureDate: parsed.departureDate ? dateFromLocalString(parsed.departureDate) : prev.departureDate,
                  returnDate: parsed.returnDate ? dateFromLocalString(parsed.returnDate) : prev.returnDate,
                  isOnlyOneWay: parsed.isOnlyOneWay !== undefined ? parsed.isOnlyOneWay : prev.isOnlyOneWay,
                  currency: parsed.currency || prev.currency || 'USD'
                };

                return newState;
              });
            }
          }
        } catch (error) {
        }
      }
    }, []);

    const lastSavedFlightFormInputRef = useRef(null);

    useEffect(() => {
      if (isClient && flightFormInput) {
        const currentKey = JSON.stringify({
          originId: flightFormInput.origin?.id,
          destinationId: flightFormInput.destination?.id,
          departureDate: dateToLocalString(flightFormInput.departureDate),
          returnDate: dateToLocalString(flightFormInput.returnDate),
          isOnlyOneWay: flightFormInput.isOnlyOneWay,
          passengers: flightFormInput.passengers?.join(',')
        });

        if (lastSavedFlightFormInputRef.current === currentKey) {
          return;
        }

        try {
          const existingFlightInfo = sessionStorage.getItem('flightInfo');
          let flightInfo = existingFlightInfo ? JSON.parse(existingFlightInfo) : {};

          if (flightFormInput.origin) {
            flightInfo.origin = {
              id: flightFormInput.origin.id,
              airportCode: flightFormInput.origin.airportCode || flightFormInput.origin.cityCode,
              airportName: flightFormInput.origin.airportName || flightFormInput.origin.cityName,
              cityCode: flightFormInput.origin.cityCode,
              cityName: flightFormInput.origin.cityName,
              countryCode: flightFormInput.origin.countryCode || flightInfo.origin?.countryCode
            };
            flightInfo.originId = flightFormInput.origin.cityCode;
            flightInfo.originName = flightFormInput.origin.cityName;
          }

          if (flightFormInput.destination) {
            flightInfo.destination = {
              id: flightFormInput.destination.id,
              airportCode: flightFormInput.destination.airportCode || flightFormInput.destination.cityCode,
              airportName: flightFormInput.destination.airportName || flightFormInput.destination.cityName,
              cityCode: flightFormInput.destination.cityCode,
              cityName: flightFormInput.destination.cityName,
              countryCode: flightFormInput.destination.countryCode || flightInfo.destination?.countryCode
            };
            flightInfo.destinationId = flightFormInput.destination.cityCode;
            flightInfo.destinationName = flightFormInput.destination.cityName;
          }

          if (flightFormInput.departureDate) {
            flightInfo.departureDate = formatDateLocal(flightFormInput.departureDate);
          }

          if (flightFormInput.isOnlyOneWay) {
            flightInfo.returnDate = null;
          } else if (flightFormInput.returnDate) {
            flightInfo.returnDate = formatDateLocal(flightFormInput.returnDate);
          }

          if (flightFormInput.passengers) {
            flightInfo.passengers = flightFormInput.passengers;
          }

          if (flightFormInput.isOnlyOneWay !== undefined) {
            flightInfo.isOnlyOneWay = flightFormInput.isOnlyOneWay;
          }

          if (flightFormInput.currency) {
            flightInfo.currency = flightFormInput.currency;
          }

          sessionStorage.setItem('flightInfo', JSON.stringify(flightInfo));
          lastSavedFlightFormInputRef.current = currentKey;
        } catch (error) {
        }
      }
    }, [flightFormInput, isMobile]);

    useEffect(() => {
      if (hasAutoSearchedRef.current) return;

      const hasUrlParams = search && search.length > 1;

      if (hasUrlParams && pathname === BUSCAR_X_VUELOS) {
        const urlParams = new URLSearchParams(search);
        const originId = urlParams.get('originId');
        const originName = urlParams.get('originName');
        const originIdn = urlParams.get('originIdn');
        const destinationId = urlParams.get('destinationId');
        const destinationName = urlParams.get('destinationName');
        const destinationIdn = urlParams.get('destinationIdn');
        const departureDate = urlParams.get('departureDate');
        const returnDate = urlParams.get('returnDate');
        const passengers = urlParams.get('passengers');
        const isOnlyOneWay = urlParams.get('isOnlyOneWay');
        const currency = urlParams.get('currency');

        setFlightFormInput((prev) => {
          const needsUpdate =
            (originId && originName && (!prev.origin || prev.origin.cityCode !== originId)) ||
            (destinationId && destinationName && (!prev.destination || prev.destination.cityCode !== destinationId)) ||
            (departureDate && (!prev.departureDate || formatDateLocal(prev.departureDate) !== departureDate)) ||
            (returnDate && (!prev.returnDate || formatDateLocal(prev.returnDate) !== returnDate)) ||
            (passengers && (!prev.passengers || prev.passengers.join(',') !== passengers)) ||
            (isOnlyOneWay !== null && prev.isOnlyOneWay !== (isOnlyOneWay === 'true')) ||
            (currency && prev.currency !== currency);

          if (!needsUpdate) return prev;

          const newState = { ...prev };

          if (originId && originName) {
            newState.origin = {
              id: originIdn && !isNaN(Number(originIdn)) ? Number(originIdn) : null,
              cityCode: originId,
              cityName: originName.trim(),
            };
          }

          if (destinationId && destinationName) {
            newState.destination = {
              id: destinationIdn && !isNaN(Number(destinationIdn)) ? Number(destinationIdn) : null,
              cityCode: destinationId,
              cityName: destinationName.trim(),
            };
          }

          if (departureDate) {
            newState.departureDate = parseDateFromURL(departureDate);
          }

          if (returnDate) {
            newState.returnDate = parseDateFromURL(returnDate);
          }

          if (passengers) {
            const parsedPassengers = passengers.split(',').map(Number);
            newState.passengers = parsedPassengers;
          }

          if (isOnlyOneWay !== null) {
            const newIsOnlyOneWay = isOnlyOneWay === 'true';
            newState.isOnlyOneWay = newIsOnlyOneWay;
            if (newIsOnlyOneWay && prev.returnDate) {
              newState.returnDate = null;
            }
            if (newIsOnlyOneWay !== prev.isOnlyOneWay) {
              setOneWayFlight(newIsOnlyOneWay);
            }
          }

          if (currency) {
            newState.currency = currency;
          }

          return newState;
        });
      }
    }, [search, pathname]);

    useImperativeHandle(ref, () => ({
      openFirstDialog: () => {
      },
      getFormInput: () => flightFormInput,
      triggerSearch: () => {
        handleSearchFlightsButton({ preventDefault: () => { } });
      },
      isFormValid: () => {
        return !!(
          flightFormInput.origin &&
          flightFormInput.destination &&
          flightFormInput.departureDate &&
          (flightFormInput.isOnlyOneWay || flightFormInput.returnDate) &&
          flightFormInput.passengers &&
          flightFormInput.passengers.length > 0
        );
      },
    }));


    const updateFlightInfo = useCallback(
      async (origin, destination, currentFlightFormInput) => {
        if (!origin || !destination || isUpdatingFlightInfoRef.current) return;

        if (isBlockedPermanentlyRef.current) {
          return;
        }

        const now = Date.now();
        const timeSinceLastCall = now - lastUpdateFlightInfoRef.current.timestamp;
        const isSameCall = 
          lastUpdateFlightInfoRef.current.originId === origin.id &&
          lastUpdateFlightInfoRef.current.destinationId === destination.id;

        if (isSameCall) {
          if (timeSinceLastCall < 5000) {
            updateFlightInfoCallCountRef.current += 1;
            updateFlightInfoMaxCallsRef.current = Math.max(
              updateFlightInfoMaxCallsRef.current,
              updateFlightInfoCallCountRef.current
            );

            if (updateFlightInfoCallCountRef.current > 3) {
              isBlockedPermanentlyRef.current = true;
              return;
            }
            return;
          } else {
            updateFlightInfoCallCountRef.current = 0;
          }
        } else {
          updateFlightInfoCallCountRef.current = 0;
        }

        try {
          const existingFlightInfo = sessionStorage.getItem('flightInfo');
          if (existingFlightInfo) {
            const parsed = JSON.parse(existingFlightInfo);
            if (
              parsed.origin?.id === origin.id &&
              parsed.destination?.id === destination.id &&
              parsed.originCountryCode &&
              parsed.destinationCountryCode
            ) {
              lastUpdateFlightInfoRef.current = {
                originId: origin.id,
                destinationId: destination.id,
                timestamp: now
              };
              return;
            }
          }
        } catch (e) {
        }

        lastUpdateFlightInfoRef.current = {
          originId: origin.id,
          destinationId: destination.id,
          timestamp: now
        };

        try {
          setIsUpdatingFlightInfo(true);
          isUpdatingFlightInfoRef.current = true;
          const [originData, destinationData] = await Promise.all([
            PackagesApi.getAirportById(origin.id),
            PackagesApi.getAirportById(destination.id),
          ]);

          if (!originData || !destinationData) {
            throw new Error('No se pudieron obtener los datos de los aeropuertos');
          }

          const isInternational = originData.countryCode !== destinationData.countryCode;
          const flightInfo = {
            originCountryCode: originData.countryCode,
            destinationCountryCode: destinationData.countryCode,
            origin: {
              id: originData.id,
              airportCode: originData.airportCode,
              airportName: originData.airportName,
              cityCode: originData.cityCode,
              cityName: originData.cityName,
              countryCode: originData.countryCode,
            },
            destination: {
              id: destinationData.id,
              airportCode: destinationData.airportCode,
              airportName: destinationData.airportName,
              cityCode: destinationData.cityCode,
              cityName: destinationData.cityName,
              countryCode: destinationData.countryCode,
            },
            departureDate: currentFlightFormInput.departureDate
              ? new Date(currentFlightFormInput.departureDate)
              : null,
            returnDate: currentFlightFormInput.isOnlyOneWay 
              ? null 
              : (currentFlightFormInput.returnDate ? new Date(currentFlightFormInput.returnDate) : null),
            passengers: currentFlightFormInput.passengers,
            isOnlyOneWay: currentFlightFormInput.isOnlyOneWay,
            isInternational,
          };

          sessionStorage.setItem('flightInfo', JSON.stringify(flightInfo));
          updateFlightInfoCallCountRef.current = 0;
          return flightInfo;
        } catch (error) {
          setErrorMessage('Error al actualizar la información del vuelo');
          setOpenToast(true);
          updateFlightInfoCallCountRef.current = 0;
        } finally {
          setIsUpdatingFlightInfo(false);
          isUpdatingFlightInfoRef.current = false;
        }
      },
      []
    );

    const handleFlightOrigin = useCallback(
      async (origin) => {
        if (!origin) return;

        if (flightFormInput?.origin?.id === origin.id) {
          return;
        }

        if (isBlockedPermanentlyRef.current) {
          setFlightFormInput((state) => ({ ...state, origin }));
          return;
        }

        setFlightFormInput((state) => ({ ...state, origin }));
      },
      [flightFormInput]
    );

    const handleFlightDestination = useCallback(
      async (destination) => {
        if (!destination) return;

        if (flightFormInput?.destination?.id === destination.id) {
          return;
        }

        if (isBlockedPermanentlyRef.current) {
          setFlightFormInput((state) => ({ ...state, destination }));
          return;
        }

        setFlightFormInput((state) => ({ ...state, destination }));
      },
      [flightFormInput]
    );

    const handleFlightPassenger = (passengers) => {
      setFlightFormInput((state) => ({ ...state, passengers }));
    };

    const handleFlightsDateRangeSelect = (departureDate, returnDate) => {
      setFlightFormInput({ ...flightFormInput, departureDate, returnDate });
    };

    const handleIsOnlyOneWay = (isOnlyOneWay) => {
      setFlightFormInput({ ...flightFormInput, isOnlyOneWay });
      setOneWayFlight(isOnlyOneWay);
    };

    const handleCurrencyChange = (currency) => {
      setFlightFormInput({ ...flightFormInput, currency });
    };

    const handleOccupationChange = (totalTourists) => {
    };

    const [isDestinationEmpty, setIsDestinationEmpty] = useState(false);
    const [isOriginEmpty, setIsOriginEmpty] = useState(false);
    const [isDatesEmpty, setIsDatesEmpty] = useState(false);
    const [isTuristasEmpty, setIsTuristasEmpty] = useState(false);

    useEffect(() => {
      if (!flightFormInput) return;

      if (flightFormInput.origin) {
        setIsOriginEmpty(false);
      }
      if (flightFormInput.destination) {
        setIsDestinationEmpty(false);
      }
      if (flightFormInput.departureDate) {
        setIsDatesEmpty(false);
      }
      if (flightFormInput.passengers && flightFormInput.passengers.length > 0) {
        setIsTuristasEmpty(false);
      }
    }, [
      flightFormInput?.origin,
      flightFormInput?.destination,
      flightFormInput?.departureDate,
      flightFormInput?.passengers,
    ]);

    const showError = useCallback((message) => {
      setErrorMessage(message);
      setOpenToast(true);
    }, []);

    const handleCloseToast = useCallback(() => {
      setOpenToast(false);
    }, []);

    const getFlightsQueryString = useCallback(() => {
      const { origin, destination, departureDate, returnDate, passengers, isOnlyOneWay, currency } =
        flightFormInput;

      const flightParams = new URLSearchParams();

      flightParams.set(
        'departureDate',
        formatWithMomentFormat(departureDate, DATE_KEYS.FORMAT.ISO)
      );

      if (returnDate && !isOnlyOneWay) {
        flightParams.set('returnDate', formatWithMomentFormat(returnDate, DATE_KEYS.FORMAT.ISO));
      }

      if (origin) {
        flightParams.set('originId', origin.cityCode);
        flightParams.set('originName', origin.cityName);
        if (origin.id && !isNaN(origin.id)) {
          flightParams.set('originIdn', origin.id);
        }
      }

      if (destination) {
        flightParams.set('destinationId', destination.cityCode);
        flightParams.set('destinationName', destination.cityName);
        if (destination.id && !isNaN(destination.id)) {
          flightParams.set('destinationIdn', destination.id);
        }
      }

      if (passengers && Array.isArray(passengers)) {
        const passengersStr = passengers.join(',');
        flightParams.set('passengers', passengersStr);
      } else {
        flightParams.set('passengers', '30');
      }

      const isOnlyOneWayValue = isOnlyOneWay ? 'true' : 'false';
      flightParams.set('isOnlyOneWay', isOnlyOneWayValue);

      if (currency) flightParams.set('currency', currency);

      flightParams.set('cache', getCacheStatus());

      return `?${flightParams.toString()}`;
    }, [flightFormInput]);

    const getFlightsQueryStringWithRefresh = useCallback(() => {
      const baseQuery = getFlightsQueryString().replace('?', '');
      const params = new URLSearchParams(baseQuery);
      params.set('_refresh', Date.now().toString());
      return `?${params.toString()}`;
    }, [getFlightsQueryString]);

    const linkToFLights = useMemo(
      () =>
        flightFormInput?.destination && flightFormInput?.origin
          ? {
            pathname: ROUTE_KEYS.BUSCAR_X_VUELOS,
          }
          : '#',
      [flightFormInput]
    );

    const handleMobileSearchComplete = () => {
      setIsMobileSearchActive(false);
      if (onCloseMobileSearch) {
        onCloseMobileSearch();
      }
      if (onSearchComplete) {
        onSearchComplete();
      }
    };

    const clearSavedState = () => {
      if (isClient) {
        try {
        } catch (error) {
        }
      }
    };

    const handleSearchFlightsButton = useCallback(
      async (e) => {
        e.preventDefault();

        if (
          !flightFormInput?.origin ||
          !flightFormInput.origin.cityCode ||
          !flightFormInput.origin.cityName
        ) {
          showError('Por favor seleccione un origen válido');
          return;
        }
        if (
          !flightFormInput?.destination ||
          !flightFormInput.destination.cityCode ||
          !flightFormInput.destination.cityName
        ) {
          showError('Por favor seleccione un destino válido');
          return;
        }
        if (
          !flightFormInput?.departureDate ||
          isNaN(new Date(flightFormInput.departureDate).getTime())
        ) {
          showError('Por favor seleccione una fecha de salida válida');
          return;
        }
        if (
          !flightFormInput?.isOnlyOneWay &&
          (!flightFormInput?.returnDate || isNaN(new Date(flightFormInput.returnDate).getTime()))
        ) {
          showError('Por favor seleccione una fecha de regreso válida');
          return;
        }
        if (
          !flightFormInput?.passengers ||
          !Array.isArray(flightFormInput.passengers) ||
          flightFormInput.passengers.length === 0
        ) {
          setFlightFormInput((prev) => ({ ...prev, passengers: [30] }));
        }

        try {
          isSearchingRef.current = true;
          
          if (onSearchStart) {
            onSearchStart();
          }

          if (!isBlockedPermanentlyRef.current && !isUpdatingFlightInfoRef.current) {
            await updateFlightInfoRef.current(
              flightFormInput.origin,
              flightFormInput.destination,
              flightFormInput
            );
          }
          setSearchTimestamp(Date.now());

          const queryStringWithRefresh = getFlightsQueryStringWithRefresh();

          if (isMobile) {
            setTimeout(() => {
              history.push(linkToFLights.pathname + queryStringWithRefresh);
              registerOnSearch();
              clearSavedState();
              handleMobileSearchComplete();
              isSearchingRef.current = false;
              isAutoSearchingRef.current = false;
            }, 500);
          } else {
            history.push(linkToFLights.pathname + queryStringWithRefresh);
            registerOnSearch();
            clearSavedState();
            isSearchingRef.current = false;
            isAutoSearchingRef.current = false;
          }
        } catch (error) {
          isSearchingRef.current = false;
          isAutoSearchingRef.current = false;
          showError(error?.message || 'Error al procesar la búsqueda');
          if (onSearchComplete) {
            onSearchComplete();
          }
        }
      },
      [
        linkToFLights,
        getFlightsQueryString,
        getFlightsQueryStringWithRefresh,
        history,
        registerOnSearch,
        flightFormInput,
        showError,
        isMobile,
        handleMobileSearchComplete,
      ]
    );

    const isSearchButtonDisabled = () => {
      return (
        !flightFormInput.origin || 
        !flightFormInput.destination || 
        !flightFormInput.departureDate
      );
    };

    const handleSearchFlightsButtonRef = useRef(handleSearchFlightsButton);
    useEffect(() => {
      handleSearchFlightsButtonRef.current = handleSearchFlightsButton;
    }, [handleSearchFlightsButton]);

    const updateFlightInfoRef = useRef(updateFlightInfo);
    useEffect(() => {
      updateFlightInfoRef.current = updateFlightInfo;
    }, [updateFlightInfo]);

    const flightFormInputRef = useRef(flightFormInput);
    useEffect(() => {
      flightFormInputRef.current = flightFormInput;
    }, [flightFormInput]);

    useEffect(() => {
      hasAutoSearchedRef.current = hasAutoSearched;
    }, [hasAutoSearched]);

    useEffect(() => {
      if (hasAutoSearchedRef.current || isMobile) return;

      const hasUrlParams = search && search.length > 1;
      if (!hasUrlParams || pathname !== BUSCAR_X_VUELOS) return;

      if (isAutoSearchingRef.current) {
        return;
      }

      if (isBlockedPermanentlyRef.current) {
        return;
      }

      if (isSearchingRef.current) {
        return;
      }

      if (isUpdatingFlightInfoRef.current) {
        return;
      }

      const currentFlightFormInput = flightFormInputRef.current;
      if (!currentFlightFormInput) return;

      const hasAllRequiredValues =
        currentFlightFormInput.origin &&
        currentFlightFormInput.destination &&
        currentFlightFormInput.departureDate &&
        currentFlightFormInput.passengers &&
        currentFlightFormInput.passengers.length > 0;

      if (!hasAllRequiredValues) return;

      const searchKey = `${search}`;
      const now = Date.now();
      const timeSinceLastAutoSearch = now - lastAutoSearchRef.current.timestamp;
      
      if (
        lastAutoSearchRef.current.params === searchKey &&
        timeSinceLastAutoSearch < 3000
      ) {
        return;
      }

      isAutoSearchingRef.current = true;
      lastAutoSearchRef.current = {
        timestamp: now,
        params: searchKey
      };
      hasAutoSearchedRef.current = true;
      setHasAutoSearched(true);

      setTimeout(() => {
        if (isAutoSearchingRef.current && !isBlockedPermanentlyRef.current && !isSearchingRef.current) {
          try {
            handleSearchFlightsButtonRef.current({ preventDefault: () => { } });
          } catch (error) {
            isSearchingRef.current = false;
            isAutoSearchingRef.current = false;
          }
        } else {
          isAutoSearchingRef.current = false;
        }
      }, 0);
    }, [search, pathname, isMobile]);

    if (isMobile) {
      return (
        <>
          <div className='package-search__form__container'>
            <div className='package-search__form__fields'>
              <FlightsFilterForm
                handleOriginSelect={handleFlightOrigin}
                handleDestinationSelect={handleFlightDestination}
                handleDateRangeSelect={handleFlightsDateRangeSelect}
                handleDepartureDateChange={handleFlightsDateRangeSelect}
                handleReturnDateChange={handleFlightsDateRangeSelect}
                handleFlightPassenger={handleFlightPassenger}
                handleIsOnlyOneWay={handleIsOnlyOneWay}
                handleCurrencyChange={handleCurrencyChange}
                handleOccupationChange={handleOccupationChange}
                formInput={flightFormInput}
                isOriginEmpty={isOriginEmpty}
                isDestinationEmpty={isDestinationEmpty}
                setIsOriginEmpty={setIsOriginEmpty}
                setIsDestinationEmpty={setIsDestinationEmpty}
                isDatesEmpty={false}
                isTuristasEmpty={false}
                setIsDatesEmpty={() => { }}
                onMobileSearchComplete={handleMobileSearchComplete}
                isMobileSearchActive={false}
                isLoading={flightContextLoading}
              />
            </div>
          </div>

          <ErrorSnackbar open={openToast} message={errorMessage} onClose={handleCloseToast} />
        </>
      );
    }

    if (!isOnTargetPath) return <></>;

    return (
      <>
        <div className='package-search__form__container'>
          <div
            className='package-search__form__fields'
            style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
          >
            <FlightsFilterForm
              handleOriginSelect={handleFlightOrigin}
              handleDestinationSelect={handleFlightDestination}
              handleDateRangeSelect={handleFlightsDateRangeSelect}
              handleDepartureDateChange={handleFlightsDateRangeSelect}
              handleReturnDateChange={handleFlightsDateRangeSelect}
              handleFlightPassenger={handleFlightPassenger}
              handleIsOnlyOneWay={handleIsOnlyOneWay}
              handleCurrencyChange={handleCurrencyChange}
              handleOccupationChange={handleOccupationChange}
              formInput={flightFormInput}
              isOriginEmpty={isOriginEmpty}
              isDestinationEmpty={isDestinationEmpty}
              setIsOriginEmpty={setIsOriginEmpty}
              setIsDestinationEmpty={setIsDestinationEmpty}
              isDatesEmpty={false}
              isTuristasEmpty={false}
              setIsDatesEmpty={() => { }}
              onMobileSearchComplete={handleMobileSearchComplete}
              isMobileSearchActive={isMobileSearchActive}
              isLoading={flightContextLoading}
            />
            <div className='package-search__button-row'>
              <button
                onClick={handleSearchFlightsButton}
                disabled={isSearchButtonDisabled() || flightContextLoading}
                className='package-search__button big-package-search__button'
                style={{
                  backgroundColor: background,
                  color: text,
                  padding: '12px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: flightContextLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  opacity: flightContextLoading ? 0.6 : 1,
                }}
              >
                <i className='fa fa-search heading__icon' />
                {flightContextLoading ? 'Buscando...' : (t('common.search') || 'Buscar')}
              </button>
            </div>
          </div>
        </div>

        <ErrorSnackbar open={openToast} message={errorMessage} onClose={handleCloseToast} />
      </>
    );
  }
);

export default FlightsSearch;
